import * as THREE from 'three';
import { SIGNAL } from '../assets/MaterialLibrary';
import type { Gate } from '../entities/CourseActors';

// 3D waypoint arrow hovering ahead of the boat, pointing at the next gate.
// Canvas-drawn chevron sprite over a glowing disc; banks with the boat heading.
export class NextGatePointer {
  readonly group = new THREE.Group();

  private readonly target = new THREE.Vector3();
  private arrowMaterial: THREE.MeshBasicMaterial | null = null;

  constructor() {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Glowing chevron pointing +Y in texture space (toward -Z when applied)
      ctx.clearRect(0, 0, 128, 128);
      ctx.strokeStyle = SIGNAL.objective;
      ctx.lineWidth = 18;
      ctx.lineCap = 'round';
      ctx.shadowColor = SIGNAL.objective;
      ctx.shadowBlur = 22;
      ctx.beginPath();
      ctx.moveTo(28, 96);
      ctx.lineTo(64, 40);
      ctx.lineTo(100, 96);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(28, 96);
      ctx.lineTo(64, 40);
      ctx.lineTo(100, 96);
      ctx.closePath();
      ctx.fillStyle = 'rgba(125, 255, 179, 0.28)';
      ctx.fill();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;

    this.arrowMaterial = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      depthTest: true, // ground-plane decal, not a floating UI slab
      side: THREE.DoubleSide,
    });
    const arrow = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1.6), this.arrowMaterial);
    // Lie flat on the water ahead of the boat, pointing at the next gate.
    this.group.add(arrow);
    this.group.visible = false;
  }

  update(boatPos: THREE.Vector3, boatHeading: number, gates: Gate[], currentGate: number, elapsed: number, finished: boolean): void {
    if (finished || currentGate >= gates.length) {
      this.group.visible = false;
      return;
    }
    const gate = gates[currentGate];
    this.target.set(gate.position.x, 0.15, gate.position.y);
    this.group.visible = true;
    const aheadX = Math.sin(boatHeading) * 9;
    const aheadZ = Math.cos(boatHeading) * 9;
    this.group.position.set(boatPos.x + aheadX, 0.18, boatPos.z + aheadZ);
    // Flat on the water, rotated toward the gate around Y only.
    const toGate = Math.atan2(gate.position.x - this.group.position.x, gate.position.y - this.group.position.z);
    this.group.rotation.set(-Math.PI / 2, 0, -(toGate - boatHeading) + Math.PI);
    // Gentle pulse; scale by distance for readability
    const dist = this.group.position.distanceTo(this.target);
    const s = THREE.MathUtils.clamp(0.8 + dist / 110, 0.8, 1.8) * (1 + Math.sin(elapsed * 3) * 0.06);
    this.group.scale.setScalar(s);
  }

  dispose(): void {
    this.arrowMaterial?.map?.dispose();
    this.arrowMaterial?.dispose();
  }
}
